import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Sprout, Scissors, Package, Truck, ArrowRight, Clock, MapPin, MessageCircle, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const HERO_IMAGE = "https://images.unsplash.com/photo-1769258958976-8852440011b8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHx2ZWdldGFibGUlMjBnYXJkZW4lMjByb3dzJTIwb3JnYW5pYyUyMGZhcm1pbmclMjBmaWVsZHxlbnwwfHx8fDE3NzE2MjQ2Njl8MA&ixlib=rb-4.1.0&q=85";
const PRODUCTS_IMAGE = "https://images.unsplash.com/photo-1754891144113-8a8d092a7dbf?crop=entropy&cs=srgb&fm=jpg&q=85";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

const steps = [
  {
    icon: Sprout,
    number: "01",
    title: "Produção",
    description: "Cultivamos os nossos produtos com métodos sustentáveis, respeitando os ciclos da natureza. Cada planta recebe o cuidado que merece.",
    details: ["Agricultura sustentável", "Respeito pela biodiversidade"]
  },
  {
    icon: Scissors,
    number: "02",
    title: "Colheita",
    description: "Os produtos são colhidos no ponto certo de maturação, no dia anterior à entrega. Isto garante que chegam à sua mesa com o máximo de frescura e sabor.",
    details: ["Colheita diária", "Ponto ótimo de maturação", "Seleção cuidada"]
  },
  {
    icon: Package,
    number: "03",
    title: "Preparação",
    description: "Cada cabaz é preparado individualmente, com carinho e atenção. Verificamos a qualidade de cada produto antes de o embalar para entrega.",
    details: ["Preparação manual", "Controlo de qualidade", "Embalagem cuidada"]
  },
  {
    icon: Truck,
    number: "04",
    title: "Entrega",
    description: "Entregamos os cabazes diretamente em sua casa, na região de Braga e Vila Verde. Garantimos que os produtos chegam frescos e em perfeitas condições.",
    details: ["Entrega ao domicílio", "Região de Braga", "Terça-feira à tarde"]
  }
];

const faqs = [
  {
    question: "Com que frequência posso receber cabazes?",
    answer: "Os nossos cabazes são preparados semanalmente. Pode optar por entregas todas as semanas ou definir uma frequência que se adapte às suas necessidades."
  },
  {
    question: "Qual é a zona de entrega?",
    answer: "Entregamos todas as terças-feiras à tarde na região de Braga e Vila Verde. Se tem dúvidas sobre a cobertura da sua zona, entre em contacto connosco para confirmarmos a disponibilidade."
  },
  {
    question: "Posso escolher o que vem no cabaz?",
    answer: "Os cabazes são preparados com os produtos disponíveis na época. No entanto, pode indicar-nos preferências ou restrições alimentares que teremos em consideração."
  },
  {
    question: "Como faço para encomendar?",
    answer: "Através da nossa loja online ou pelas nossas redes sociais (Instagram). Escolhe os produtos, indica a sua morada e confirma a data — as entregas são realizadas às terças-feiras à tarde."
  },
  {
    question: "Qual é o prazo de entrega após a encomenda?",
    answer: "As entregas são realizadas à terça-feira à tarde seguinte à confirmação da encomenda, na região de Braga e Vila Verde."
  }
];

export default function HowItWorksPage() {
  return (
    <div data-testid="how-it-works-page" className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center pt-20">
        <div className="absolute inset-0 z-0">
          <img
            src={HERO_IMAGE}
            alt="Mãos a colher produtos"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#3A4A38]/90 via-[#3A4A38]/70 to-[#3A4A38]/50" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl text-white"
          >
            <span className="font-accent text-2xl text-[#D4C4A8]">
              Simples e Transparente
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold leading-tight mt-2 mb-6">
              Como Funciona
            </h1>
            <p className="text-lg text-white/80">
              Do campo para a sua mesa em apenas quatro passos simples.
              Conheça o nosso processo de produção e entrega.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="font-accent text-2xl text-[#1B93A4]">
              O Processo
            </span>
            <h2 className="text-3xl lg:text-4xl font-semibold text-[#2C2C2C] mt-2">
              Da terra à sua mesa
            </h2>
          </motion.div>

          <div className="space-y-12 lg:space-y-16">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`grid lg:grid-cols-2 gap-8 lg:gap-16 items-center ${
                  index % 2 === 1 ? "lg:flex-row-reverse" : ""
                }`}
                data-testid={`step-section-${index}`}
              >
                <div className={index % 2 === 1 ? "lg:order-2" : ""}>
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-16 h-16 bg-[#3A4A38]/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <step.icon className="w-8 h-8 text-[#3A4A38]" />
                    </div>
                    <div>
                      <span className="text-4xl font-semibold text-[#D4C4A8]">
                        {step.number}
                      </span>
                      <h3 className="text-2xl font-semibold text-[#2C2C2C]">
                        {step.title}
                      </h3>
                    </div>
                  </div>

                  <p className="text-[#2C2C2C]/80 leading-relaxed mb-6">
                    {step.description}
                  </p>

                  <ul className="space-y-2">
                    {step.details.map((detail, dIndex) => (
                      <li key={dIndex} className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-[#1B93A4]" />
                        <span className="text-sm text-[#2C2C2C]/70">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className={`${index % 2 === 1 ? "lg:order-1" : ""}`}>
                  <Card className="bg-[#F3F1EB] border-none overflow-hidden">
                    <CardContent className="p-8 lg:p-12 text-center">
                      <step.icon className="w-24 h-24 mx-auto text-[#3A4A38]/30" />
                    </CardContent>
                  </Card>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Delivery Info Section */}
      <section className="py-20 lg:py-28 bg-[#F3F1EB] noise-overlay">
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="image-hover rounded-xl overflow-hidden"
            >
              <img
                src={PRODUCTS_IMAGE}
                alt="Produtos frescos prontos para entrega"
                className="w-full h-[400px] lg:h-[500px] object-cover"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="font-accent text-2xl text-[#1B93A4]">
                Informações
              </span>
              <h2 className="text-3xl lg:text-4xl font-semibold text-[#2C2C2C] mt-2 mb-6">
                Detalhes de Entrega
              </h2>
              <div className="gold-line mb-8" />

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
                    <MapPin className="w-6 h-6 text-[#3A4A38]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#2C2C2C] mb-1">
                      Zona de Entrega
                    </h4>
                    <p className="text-[#2C2C2C]/70 text-sm">
                      Região de Braga e Vila Verde. Entregas locais ao domicílio.
                      Contacte-nos para confirmar a disponibilidade na sua área.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
                    <Clock className="w-6 h-6 text-[#3A4A38]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#2C2C2C] mb-1">
                      Frequência das Entregas
                    </h4>
                    <p className="text-[#2C2C2C]/70 text-sm">
                      Entregamos todas as terças-feiras à tarde na região de
                      Braga e Vila Verde.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
                    <MessageCircle className="w-6 h-6 text-[#3A4A38]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#2C2C2C] mb-1">
                      Como Encomendar
                    </h4>
                    <p className="text-[#2C2C2C]/70 text-sm">
                      Através da nossa loja online ou redes sociais (Instagram).
                      Escolhe os produtos em poucos cliques.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-8 p-4 bg-white rounded-xl border border-[#D4C4A8]/50">
                <p className="text-sm text-[#2C2C2C]/70 text-center">
                  <span className="font-medium text-[#2C2C2C]">Nota:</span> Confirme
                  sempre a disponibilidade por mensagem antes de encomendar.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="font-accent text-2xl text-[#1B93A4]">
              Dúvidas Frequentes
            </span>
            <h2 className="text-3xl lg:text-4xl font-semibold text-[#2C2C2C] mt-2">
              Perguntas Frequentes
            </h2>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="max-w-3xl mx-auto space-y-4"
          >
            {faqs.map((faq, index) => (
              <motion.div key={index} variants={fadeInUp}>
                <Card
                  className="bg-[#F3F1EB] border-none"
                  data-testid={`faq-${index}`}
                >
                  <CardContent className="p-6">
                    <h4 className="font-semibold text-[#2C2C2C] mb-2">
                      {faq.question}
                    </h4>
                    <p className="text-[#2C2C2C]/70 text-sm leading-relaxed">
                      {faq.answer}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 bg-[#3A4A38] text-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-2xl mx-auto"
          >
            <span className="font-accent text-2xl text-[#D4C4A8]">
              Pronto para começar?
            </span>
            <h2 className="text-3xl lg:text-4xl font-semibold mt-2 mb-6">
              Faça a sua primeira encomenda
            </h2>
            <p className="text-white/80 mb-8">
              Agora que já conhece o nosso processo, está na hora de experimentar
              os nossos produtos. Entre em contacto e receba frescura em sua casa.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contacto" data-testid="how-final-cta">
                <Button className="bg-white text-[#3A4A38] hover:bg-white/90 rounded-full px-8 py-6 text-base font-medium">
                  Encomendar Cabaz
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/produtos" data-testid="how-products-cta">
                <Button
                  variant="outline"
                  className="border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-base font-medium"
                >
                  Ver Cabazes
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
